/**
 * WEB-926, позиция 1 — механика стирания скретч-карты, вынесенная из
 * компонента, чтобы её можно было проверить без DOM.
 *
 * Реализация НЕ на canvas намеренно: canvas в jsdom мёртв (getContext → null),
 * а замер стёртой площади через getImageData — дорогой на каждом кадре. Здесь
 * поверхность разбита на грубую сетку ячеек; ячейка, по которой прошёл
 * указатель, «стирается» (её плитка убирается), а мягкий blur на слое плиток
 * превращает прямоугольные дырки в органичные проплешины.
 */

// WEB-957: сетка осталась ТОЛЬКО счётчиком стёртой площади (вид рисует
// SVG-маска следа в ScratchCardBlock) и помельчала, чтобы процент рос плавно.
export const SCRATCH_COLS = 16;
export const SCRATCH_ROWS = 10;
export const SCRATCH_CELL_COUNT = SCRATCH_COLS * SCRATCH_ROWS;

/** Порог раскрытия по умолчанию (%), если автор не задал свой. */
export const SCRATCH_DEFAULT_THRESHOLD = 45;
/** Границы порога — ниже 10% карта раскрывается от случайного касания. */
export const SCRATCH_MIN_THRESHOLD = 10;
export const SCRATCH_MAX_THRESHOLD = 90;

export interface ScratchRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Индекс ячейки под точкой (координаты окна) или null, если точка вне
 * поверхности либо поверхность ещё не имеет размеров (первый кадр монтирования).
 */
export function scratchCellAt(
  rect: ScratchRect,
  clientX: number,
  clientY: number,
): number | null {
  if (rect.width <= 0 || rect.height <= 0) return null;
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null;
  const col = Math.min(SCRATCH_COLS - 1, Math.floor((x / rect.width) * SCRATCH_COLS));
  const row = Math.min(SCRATCH_ROWS - 1, Math.floor((y / rect.height) * SCRATCH_ROWS));
  return row * SCRATCH_COLS + col;
}

/** Доля стёртой площади в процентах. */
export function scratchPercent(cells: ReadonlySet<number>): number {
  return Math.round((cells.size / SCRATCH_CELL_COUNT) * 100);
}

/** Порог автора, приведённый к допустимому диапазону. */
export function scratchThreshold(value: number | undefined): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return SCRATCH_DEFAULT_THRESHOLD;
  return Math.min(SCRATCH_MAX_THRESHOLD, Math.max(SCRATCH_MIN_THRESHOLD, Math.round(value)));
}

// ─── WEB-957: след стирания ────────────────────────────────────────────────

/** Точка следа в пикселях поверхности (координаты локальные, не окна). */
export interface ScratchPoint {
  x: number;
  y: number;
}

/** Один штрих = одно нажатие; между штрихами перемычек нет. */
export type ScratchStroke = ScratchPoint[];

/** Диаметр кисти следа, px — примерно палец. */
export const SCRATCH_BRUSH_SIZE = 34;
/** Минимальный шаг между точками следа, px — защита от разбухания массива. */
export const SCRATCH_TRAIL_MIN_STEP = 3;
/** Потолок точек следа: дальше след не детализируем (счётчик площади работает всегда). */
export const SCRATCH_TRAIL_MAX_POINTS = 1500;

/** Точка указателя в координатах поверхности или null вне её. */
export function scratchPointAt(
  rect: ScratchRect,
  clientX: number,
  clientY: number,
): ScratchPoint | null {
  if (rect.width <= 0 || rect.height <= 0) return null;
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null;
  return { x: Math.round(x), y: Math.round(y) };
}

/**
 * Добавить точку в ПОСЛЕДНИЙ штрих (иммутабельно). Слишком близкая к
 * предыдущей или сверх потолка — отбрасывается (след уже непрерывен).
 */
export function appendTrailPoint(
  strokes: ReadonlyArray<ScratchStroke>,
  point: ScratchPoint,
): ScratchStroke[] {
  const total = strokes.reduce((n, st) => n + st.length, 0);
  if (total >= SCRATCH_TRAIL_MAX_POINTS) return strokes as ScratchStroke[];
  if (strokes.length === 0) return [[point]];
  const last = strokes[strokes.length - 1];
  const prev = last[last.length - 1];
  if (
    prev &&
    Math.abs(prev.x - point.x) < SCRATCH_TRAIL_MIN_STEP &&
    Math.abs(prev.y - point.y) < SCRATCH_TRAIL_MIN_STEP
  ) {
    return strokes as ScratchStroke[];
  }
  return [...strokes.slice(0, -1), [...last, point]];
}
